// The Workflows listing table. Full width at all times — the configuration panel
// overlays it rather than docking beside it, so the table is never designed around
// the panel's width.

import { Box, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ThemedCheckbox } from '@/design-system/components/ThemedCheckbox';
import { Chatbot } from '@/lib/sdk/chatbot/types';
import { WorkflowTableRow } from './WorkflowTableRow';

// Matches design-system/components/DataTable: the `simple` table variant supplies the
// brand header band, and the theme's Table baseStyle supplies sentence-case / sm / 600.
// Only the text colour, border and sticky positioning are set here — overriding
// textTransform or fontSize is what made this table look foreign to the rest of the app.
const HEAD_PROPS = {
  position: 'sticky' as const,
  top: 0,
  zIndex: 1,
  color: 'text-primary',
  borderColor: 'border-default',
  // size="sm" gives the header the same tight padding as the rows; the header band
  // needs more room than a data row to read as a header.
  py: 4,
};

interface WorkflowTableProps {
  rows: Chatbot[];
  checkedIds: string[];
  openId: string | undefined;
  /** Drag is offered only under Custom order with no active search — otherwise the
   *  order written would come from a view the user cannot see. */
  canReorder: boolean;
  reminderSummary: (chatbot: Chatbot) => string;
  onCheck: (id: string) => void;
  onCheckAll: (checked: boolean) => void;
  onConfigure: (chatbot: Chatbot) => void;
  onCopyCommand: (chatbot: Chatbot) => void;
  onShowQr: (chatbot: Chatbot) => void;
  onReorder: (activeId: string, overId: string) => void;
}

export function WorkflowTable({
  rows,
  checkedIds,
  openId,
  canReorder,
  reminderSummary,
  onCheck,
  onCheckAll,
  onConfigure,
  onCopyCommand,
  onShowQr,
  onReorder,
}: WorkflowTableProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const allChecked = rows.length > 0 && checkedIds.length === rows.length;
  const someChecked = checkedIds.length > 0 && !allChecked;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder(String(active.id), String(over.id));
  };

  const body = rows.map((chatbot, index) => (
    <WorkflowTableRow
      key={chatbot.id}
      chatbot={chatbot}
      position={index + 1}
      isChecked={checkedIds.includes(chatbot.id)}
      isOpen={chatbot.id === openId}
      isDraggable={canReorder}
      reminderSummary={reminderSummary(chatbot)}
      onCheck={onCheck}
      onConfigure={onConfigure}
      onCopyCommand={onCopyCommand}
      onShowQr={onShowQr}
    />
  ));

  return (
    <Box
      borderWidth="1px"
      borderColor="border-default"
      borderRadius="lg"
      bg="bg-surface"
      overflowX="auto"
      sx={{
        '&::-webkit-scrollbar': { height: '6px', width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--chakra-colors-gray-400)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': { background: 'var(--chakra-colors-gray-500)' },
      }}
    >
      {/* size="sm" is the one deliberate difference from DataTable: this is a dense
          listing built to show ~50 workflows, not a 10-row paginated table. */}
      <Table size="sm" variant="simple" data-testid="wfl-table">
        <Thead>
          <Tr>
            {/* Left gutter: select-all, then per-row reorder + configure. */}
            <Th {...HEAD_PROPS} w="150px">
              <ThemedCheckbox
                data-testid="wfl-select-all"
                isChecked={allChecked}
                isIndeterminate={someChecked}
                onChange={(event) => onCheckAll(event.target.checked)}
                aria-label="Select all workflows"
              />
            </Th>
            <Th {...HEAD_PROPS} minW="200px">Workflow</Th>
            <Th {...HEAD_PROPS} w="150px">Department</Th>
            <Th {...HEAD_PROPS} w="170px">Command</Th>
            <Th {...HEAD_PROPS} w="180px">Type</Th>
            <Th {...HEAD_PROPS} w="110px">Tasks</Th>
            <Th {...HEAD_PROPS} w="170px">Reminders</Th>
            {/* Absorbs the slack so the data columns stay packed together instead of
                the Workflow column stretching, and QR still pins to the right edge. */}
            <Th {...HEAD_PROPS} />
            <Th {...HEAD_PROPS} w="70px">QR</Th>
          </Tr>
        </Thead>
        <Tbody>
          {canReorder ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rows.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
              >
                {body}
              </SortableContext>
            </DndContext>
          ) : (
            body
          )}
        </Tbody>
      </Table>

      {rows.length === 0 && (
        <Box px={4} py={10}>
          <Text fontSize="sm" color="text-secondary" textAlign="center">
            No workflows in this tab.
          </Text>
        </Box>
      )}
    </Box>
  );
}
